import request from 'utils/request'
import { modalActions } from 'store/modal'
import { logSentryEvent } from 'utils/sentry'
import { MAX_STUDIES_ON_PAGE, STUDY_SORTING_OPTIONS } from 'utils/constants'
import { _deleteStudy, uploadCustomLogo } from '../../routes/CreateStudy/modules/CreateStudy'

const FETCHED_STUDIES = 'FETCHED_STUDIES'
const FETCHED_STUDIES_PAGE = 'FETCHED_STUDIES_PAGE'
const DELETE_STUDY_PREVIEW_LOGO = 'DELETE_STUDY_PREVIEW_LOGO'
const ADD_STUDY_PREVIEW_LOGO = 'ADD_STUDY_PREVIEW_LOGO'

const SORT_API_MAP = {
  [STUDY_SORTING_OPTIONS.studyCreateDate]: 'created_on',
  [STUDY_SORTING_OPTIONS.studyName]: 'name',
}

export const DEFAULT_STUDIES_PAGE = {
  items: [],
  pagination: {
    page: 1,
    page_size: MAX_STUDIES_ON_PAGE,
    total: 0,
    total_pages: 0,
  },
}

let studiesPageFetchGeneration = 0

export const buildStudiesListQuery = ({ page, pageSize, search, sort, order, features } = {}) => {
  const params = new URLSearchParams()
  params.set('page', String(page || 1))
  params.set('page_size', String(pageSize || MAX_STUDIES_ON_PAGE))
  if (search) params.set('search', search)
  if (sort) params.set('sort', SORT_API_MAP[sort] || sort)
  if (order) params.set('order', order)
  if (features?.length) {
    const featureValues = features.map(feature => feature.value || feature).filter(Boolean)
    if (featureValues.length) params.set('features', featureValues.join(','))
  }
  return params.toString()
}

export const uploadLogo = (formData, { method }) => {
  const studyId = formData.get('study_id')
  const url = `/control/studies/${studyId}/white-label-logo`
  return dispatch => {
    return dispatch(
      request({
        method,
        url,
        contentType: 'multipart/form-data',
        body: formData,
        loadingKey: 'mediaUpload',
        hasLoader: true,
        success: () => {
          dispatch(modalActions.closeModal())
          return dispatch(
            request({
              method: 'GET',
              url,
              success: payload => {
                dispatch(uploadCustomLogo(payload.url))
                return payload
              },
              fail: (_, content) => {
                logSentryEvent({
                  message: 'Fetch study white-label logo failed',
                  level: 'error',
                  tags: {
                    module: 'studies',
                    action: 'fetchLogo',
                  },
                  contexts: {
                    study: {
                      studyId,
                      errorMessage: content?.message,
                    },
                  },
                })
              },
              catchMessage: 'There was a problem getting the logo.',
              skipNotySuccess: true,
            }),
          )
        },
        successMessage: `Logo successfully ${method === 'POST' ? 'uploaded' : 'changed'}.`,
        catchMessage: `There was a problem ${
          method === 'POST' ? 'uploading' : 'changing'
        } the logo. Please try again later.`,
      }),
    )
  }
}

const deletePreviewLogo = studyId => {
  return {
    type: DELETE_STUDY_PREVIEW_LOGO,
    studyId,
  }
}

const deleteLogo = studyId => {
  const url = `/control/studies/${studyId}/white-label-logo`
  return dispatch => {
    return dispatch(
      request({
        method: 'DELETE',
        url,
        success: () => dispatch(deletePreviewLogo(studyId)),
        fail: (_, content) => {
          logSentryEvent({
            message: 'Delete study white-label logo failed',
            level: 'error',
            tags: {
              module: 'studies',
              action: 'deleteLogo',
            },
            contexts: {
              study: {
                studyId,
                errorMessage: content?.message,
              },
            },
          })
        },
        successMessage: 'Logo successfully deleted.',
        catchMessage: 'There was a problem deleting the logo. Please try again later.',
      }),
    )
  }
}

const addPreviewLogo = ({ studyId, url }) => {
  return {
    type: ADD_STUDY_PREVIEW_LOGO,
    studyId,
    url,
  }
}

export const fetchStudies = (hasLoader = true) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: '/control/admin/studies',
        errorMessage: 'error fetching studies',
        success: json => {
          dispatch({
            type: FETCHED_STUDIES,
            payload: json.studies,
          })
        },
        hasLoader,
        forceLoader: hasLoader,
      }),
    )
  }
}

export const fetchPaginatedStudies = ({
  page = 1,
  pageSize = MAX_STUDIES_ON_PAGE,
  search = '',
  sort = STUDY_SORTING_OPTIONS.studyCreateDate,
  order = 'asc',
  features = [],
} = {}) => {
  return dispatch => {
    const generation = ++studiesPageFetchGeneration
    const query = buildStudiesListQuery({ page, pageSize, search, sort, order, features })
    return dispatch(
      request({
        method: 'GET',
        url: `/control/v2/admin/studies?${query}`,
        errorMessage: 'error fetching studies',
        success: json => {
          if (generation !== studiesPageFetchGeneration) return
          dispatch({
            type: FETCHED_STUDIES_PAGE,
            payload: {
              items: json.studies || [],
              pagination: json.pagination || {
                page,
                page_size: pageSize,
                total: (json.studies || []).length,
                total_pages: 1,
              },
            },
          })
        },
        hasLoader: true,
        loadingKey: 'studiesList',
      }),
    )
  }
}

const deleteStudy = id => dispatch => {
  return dispatch(
    _deleteStudy(id, () => {
      return dispatch(fetchStudies())
    }),
  )
}

export const studies = (state = [], action) => {
  switch (action.type) {
    case FETCHED_STUDIES:
      return action.payload

    case DELETE_STUDY_PREVIEW_LOGO: {
      const newState = [...state]
      return newState.map(study => {
        if (study.id === action.studyId) delete study['custom-logo']
        return study
      })
    }

    case ADD_STUDY_PREVIEW_LOGO: {
      const newState = [...state]
      return newState.map(study => {
        if (study.id === action.studyId && action.url) study['custom-logo'] = action.url
        return study
      })
    }

    default:
      return state
  }
}

const applyLogoToPageItems = (items, action) => {
  return items.map(study => {
    if (study.id !== action.studyId) return study
    const nextStudy = { ...study }
    if (action.type === DELETE_STUDY_PREVIEW_LOGO) {
      delete nextStudy['custom-logo']
    } else if (action.url) {
      nextStudy['custom-logo'] = action.url
    }
    return nextStudy
  })
}

export const studiesPage = (state = DEFAULT_STUDIES_PAGE, action) => {
  switch (action.type) {
    case FETCHED_STUDIES_PAGE:
      return {
        ...state,
        items: action.payload.items,
        pagination: action.payload.pagination,
      }
    case DELETE_STUDY_PREVIEW_LOGO:
    case ADD_STUDY_PREVIEW_LOGO:
      return {
        ...state,
        items: applyLogoToPageItems(state.items, action),
      }
    default:
      return state
  }
}

export const studiesAction = {
  closeModal: modalActions.closeModal,
  openModal: modalActions.openModal,
  deleteLogo,
  addPreviewLogo,
  uploadLogo,
  fetchStudies,
  fetchPaginatedStudies,
  deleteStudy,
}
export default studies
